/** Простой текст для UI: **жирный**, *курсив*, ${dealershipName}. Пустая строка = отступ. */
export const DEALERSHIP_NAME_TAG = "${dealershipName}";

export const PLAIN_SUBJECT_PREFIX = "Reminder: products not ordered";

export const PLAIN_TEXT_BEFORE = `Hello!

We noticed that your dealership ${DEALERSHIP_NAME_TAG} has not ordered the following products for a while:`;

export const PLAIN_TEXT_AFTER =
    "If you would like to place an order, please visit our website or reply to this email.\n\nThank you!";

const COMPANY_FOOTER_HTML =
    '<p class="muted">${companyName!\'\'} | ${companyPhone!\'\'} | <a href="mailto:${companyEmail!\'\'}">${companyEmail!\'\'}</a></p>';

function applyDealershipTagInLine(line: string): string {
    if (line.includes("<strong>${dealershipName")) {
        return line;
    }
    return line
        .replace(/\$\{dealershipName!''\}/g, "<strong>${dealershipName!''}</strong>")
        .replace(/\$\{dealershipName\}/g, "<strong>${dealershipName!''}</strong>");
}

/** *курсив* и **жирный** в простом тексте → HTML (сначала курсив, чтобы не ломать **). */
export function applyInlineFormatting(line: string): string {
    let s = line;
    s = s.replace(/(?<!\*)\*(?!\*)([^*]+?)(?<!\*)\*(?!\*)/g, "<em>$1</em>");
    s = s.replace(/\*\*([^*]+?)\*\*/g, "<strong>$1</strong>");
    return applyDealershipTagInLine(s);
}

/**
 * Одна пустая строка в plain → &lt;p&gt;&amp;nbsp;&lt;/p&gt; (видимый перенос в превью и в письме).
 * Соседние непустые строки без пустой между ними → один &lt;p&gt; с &lt;br/&gt;.
 */
export function paragraphsToHtml(plain: string): string {
    const normalized = plain.replace(/\r\n/g, "\n");
    const lines = normalized.split("\n");
    const parts: string[] = [];

    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        if (line.trim() === "") {
            parts.push("<p>&nbsp;</p>");
            continue;
        }
        const textLines: string[] = [line];
        while (i + 1 < lines.length && lines[i + 1].trim() !== "") {
            i++;
            textLines.push(lines[i]);
        }
        const inner = textLines.map((l) => applyInlineFormatting(l)).join("<br/>");
        parts.push(`<p>${inner}</p>`);
    }

    return parts.join("\n");
}

/** Plain → HTML для сохранения (пустое поле остаётся пустым, без дефолтов). */
export function plainBeforeToHtml(plain: string): string {
    if (!plain?.trim()) {
        return "";
    }
    return paragraphsToHtml(plain);
}

/** Для превью: только введённый текст (без подстановки дефолта). */
export function plainBeforeToHtmlForPreview(plain: string): string {
    if (!plain.trim()) {
        return "";
    }
    return paragraphsToHtml(plain);
}

export function plainAfterToHtml(plain: string): string {
    if (!plain?.trim()) {
        return "";
    }
    return paragraphsToHtml(plain);
}

export function plainAfterToHtmlForPreview(plain: string): string {
    return plainAfterToHtml(plain);
}

function isEmptyParagraphInner(inner: string): boolean {
    const text = inner
        .replace(/<br\s*\/?>/gi, "")
        .replace(/&nbsp;|&#160;|\u00a0/gi, " ")
        .replace(/<[^>]+>/g, "")
        .trim();
    return text === "";
}

function paragraphInnerToPlain(inner: string): string {
    let s = inner;
    s = s.replace(/<strong>\$\{dealershipName!''\}<\/strong>/gi, DEALERSHIP_NAME_TAG);
    s = s.replace(/<strong>(.*?)<\/strong>/gi, "**$1**");
    s = s.replace(/<em>(.*?)<\/em>/gi, "*$1*");
    s = s.replace(/<br\s*\/?>/gi, "\n");
    s = s.replace(/<[^>]+>/g, "");
    s = s.replace(/\$\{dealershipName(?:!'')?\}/g, DEALERSHIP_NAME_TAG);
    return s.replace(/\n+$/, "");
}

/** HTML → plain: каждый &lt;p&gt; — строка или пустая строка; &lt;br/&gt; внутри абзаца — перенос. */
function htmlToPlain(html: string): string {
    const stripped = html.trim();
    if (!stripped) {
        return "";
    }

    const paragraphRe = /<p(\s[^>]*)?>([\s\S]*?)<\/p>/gi;
    const lines: string[] = [];
    let match: RegExpExecArray | null;
    let found = false;

    while ((match = paragraphRe.exec(stripped)) !== null) {
        found = true;
        const inner = match[2];
        if (isEmptyParagraphInner(inner)) {
            lines.push("");
        } else {
            lines.push(paragraphInnerToPlain(inner));
        }
    }

    if (!found) {
        let s = stripped;
        s = s.replace(/<strong>\$\{dealershipName!''\}<\/strong>/gi, DEALERSHIP_NAME_TAG);
        s = s.replace(/<strong>(.*?)<\/strong>/gi, "**$1**");
        s = s.replace(/<em>(.*?)<\/em>/gi, "*$1*");
        s = s.replace(/<br\s*\/?>/gi, "\n");
        s = s.replace(/<[^>]+>/g, "");
        s = s.replace(/\$\{dealershipName(?:!'')?\}/g, DEALERSHIP_NAME_TAG);
        return s.replace(/^\n+/, "");
    }

    return lines.join("\n").replace(/^\n+/, "");
}

/** Убирает head/style и обёртку документа из HTML-фрагмента. */
export function stripEmailDocumentWrapper(html: string): string {
    return html
        .replace(/<!DOCTYPE[\s\S]*?>/gi, "")
        .replace(/<head[\s\S]*?<\/head>/gi, "")
        .replace(/<style[\s\S]*?<\/style>/gi, "")
        .replace(/<title[\s\S]*?<\/title>/gi, "")
        .replace(/<\/?html[^>]*>/gi, "")
        .replace(/<\/?body[^>]*>/gi, "")
        .replace(/body\s*\{[\s\S]*?\.muted\s*\{[^}]*\}\s*/gi, "")
        .trim();
}

export function extractEmailBodyInnerHtml(html: string): string {
    const lower = html.toLowerCase();
    const bodyStart = lower.indexOf("<body");
    if (bodyStart === -1) {
        return stripEmailDocumentWrapper(html);
    }
    const bodyTagEnd = html.indexOf(">", bodyStart);
    if (bodyTagEnd === -1) {
        return stripEmailDocumentWrapper(html);
    }
    const bodyEnd = lower.lastIndexOf("</body>");
    const inner = bodyEnd > bodyTagEnd ? html.slice(bodyTagEnd + 1, bodyEnd) : html.slice(bodyTagEnd + 1);
    return stripEmailDocumentWrapper(inner);
}

export function stripCompanyFooterFromHtml(html: string): string {
    return html.replace(/(?:\s*<p\s+class="muted">[\s\S]*?<\/p>)+\s*$/i, "").trim();
}

function htmlFragmentToPlain(html: string): string {
    const cleaned = stripEmailDocumentWrapper(html);
    if (!cleaned.trim()) {
        return "";
    }
    return htmlToPlain(cleaned);
}

export function htmlBeforeToPlain(html: string): string {
    const plain = htmlFragmentToPlain(html);
    return plain || PLAIN_TEXT_BEFORE;
}

export function htmlAfterToPlain(html: string): string {
    const plain = htmlFragmentToPlain(stripCompanyFooterFromHtml(html));
    return plain || PLAIN_TEXT_AFTER;
}

export function subjectPlainToTemplate(plainPrefix: string): string {
    const p = plainPrefix.trim() || PLAIN_SUBJECT_PREFIX;
    if (p.includes("${dealershipName}")) return p;
    return `${p} — \${dealershipName}`;
}

export function subjectTemplateToPlain(template: string): string {
    if (!template.trim()) return PLAIN_SUBJECT_PREFIX;
    return template.replace(/\$\{dealershipName(?:!'')?\}/g, DEALERSHIP_NAME_TAG).trim();
}

export function insertTextAtSelection(
    value: string,
    insertion: string,
    selectionStart: number,
    selectionEnd: number,
): {nextValue: string; cursor: number} {
    const nextValue = value.slice(0, selectionStart) + insertion + value.slice(selectionEnd);
    return {nextValue, cursor: selectionStart + insertion.length};
}

export function wrapSelectionWithMarkers(
    value: string,
    selectionStart: number,
    selectionEnd: number,
    before: string,
    after: string,
): {nextValue: string; cursor: number} | null {
    if (selectionStart === selectionEnd) {
        return null;
    }
    const selected = value.slice(selectionStart, selectionEnd);
    const nextValue = value.slice(0, selectionStart) + before + selected + after + value.slice(selectionEnd);
    const cursor = selectionStart + before.length + selected.length + after.length;
    return {nextValue, cursor};
}

const PRODUCTS_LIST_FREEMARKER_RE = /<ul>\s*<#list\s+products\s+as\s+product>[\s\S]*?<\/#list>\s*<\/ul>/i;
const PRODUCTS_LIST_RENDERED_RE = /<ul\b[^>]*>[\s\S]*?<\/ul>/i;

/** Разбивает сохранённый полный HTML письма на plain-тексты для формы редактора. */
export function splitInactiveOrderReminderEmailBodyToPlain(fullBody: string): {
    textBefore: string;
    textAfter: string;
} {
    if (!fullBody?.trim()) {
        return {textBefore: "", textAfter: ""};
    }
    const bodyInner = extractEmailBodyInnerHtml(fullBody);
    const listMatch =
        bodyInner.match(PRODUCTS_LIST_FREEMARKER_RE) ?? bodyInner.match(PRODUCTS_LIST_RENDERED_RE);
    if (!listMatch || listMatch.index === undefined) {
        return {
            textBefore: htmlFragmentToPlain(bodyInner),
            textAfter: "",
        };
    }
    const beforeHtml = bodyInner.slice(0, listMatch.index);
    const afterHtml = bodyInner.slice(listMatch.index + listMatch[0].length);
    return {
        textBefore: htmlFragmentToPlain(beforeHtml),
        textAfter: htmlFragmentToPlain(stripCompanyFooterFromHtml(afterHtml)),
    };
}

export function withInactiveOrderReminderPlainDefaults(
    subject?: string | null,
    textBefore?: string | null,
    textAfter?: string | null,
): {subject: string; textBefore: string; textAfter: string} {
    return {
        subject: subject?.trim() ? subjectTemplateToPlain(subject) : PLAIN_SUBJECT_PREFIX,
        textBefore: textBefore?.trim()
            ? (textBefore.includes("<") ? htmlBeforeToPlain(textBefore) : textBefore)
            : PLAIN_TEXT_BEFORE,
        textAfter: textAfter?.trim()
            ? (textAfter.includes("<") ? htmlAfterToPlain(textAfter) : textAfter)
            : PLAIN_TEXT_AFTER,
    };
}
